'use client'

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdAdd, MdDescription, MdSearch, MdClose } from 'react-icons/md'
import {
    getEvolutions,
    NursingEvolution,
    EVOLUTION_STATUS_LABELS,
    EVOLUTION_STATUS_COLORS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function NursingEvolutionPage() {
    const navigate = useNavigate()
    const [evolutions, setEvolutions] = useState<NursingEvolution[]>([])
    const [filteredEvolutions, setFilteredEvolutions] = useState<
        NursingEvolution[]
    >([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    useEffect(() => {
        loadEvolutions()
    }, [selectedHospitalId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = evolutions.filter(e =>
                e.admission?.client.name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
            )
            setFilteredEvolutions(filtered)
        } else {
            setFilteredEvolutions(evolutions)
        }
    }, [searchTerm, evolutions])

    const loadEvolutions = async () => {
        setLoading(true)
        try {
            const response = await getEvolutions(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setEvolutions(response.result)
                setFilteredEvolutions(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar evoluções')
            }
        } catch (error) {
            toast.error('Erro ao carregar evoluções')
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando evoluções...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg">
                        <MdDescription className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Evolução de Enfermagem
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Registros de evolução dos pacientes
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/hospital/evolucoes/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Nova</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por paciente..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredEvolutions.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdDescription className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma evolução encontrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Comece registrando evoluções dos pacientes
                        </p>
                        <button
                            onClick={() => navigate('/hospital/evolucoes/new')}
                            className="btn btn-secondary gap-2"
                        >
                            <MdAdd className="text-xl" />
                            Nova evolução
                        </button>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Leito</th>
                                <th>Data</th>
                                <th>Turno</th>
                                <th>Enfermeiro</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEvolutions.map(evolution => (
                                <tr
                                    key={evolution.id}
                                    className="hover cursor-pointer"
                                    onClick={() =>
                                        navigate(
                                            `/hospital/evolucoes/${evolution.id}`
                                        )
                                    }
                                >
                                    <td>
                                        <div className="font-bold">
                                            {evolution.admission?.client.name}
                                        </div>
                                    </td>
                                    <td>
                                        {evolution.admission?.bed?.number ||
                                            '-'}
                                    </td>
                                    <td>
                                        {new Date(
                                            evolution.evolutionDate
                                        ).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td>{evolution.shift || '-'}</td>
                                    <td>{evolution.nurse?.name || '-'}</td>
                                    <td>
                                        <span
                                            className={`badge ${EVOLUTION_STATUS_COLORS[evolution.status]}`}
                                        >
                                            {
                                                EVOLUTION_STATUS_LABELS[
                                                    evolution.status
                                                ]
                                            }
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
